import React, { useState, useEffect } from 'react';
import patternsRadarService from '../../../services/patternsRadarService';
import DoubleScrollTable from './DoubleScrollTable';
import {
  formatPercent,
  formatCurrency,
  toNum,
  matchesNumericRange,
} from '../patternsRadarUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faSpinner,
  faChartLine,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';

const ALPHABET = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
  'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', '0-9',
];

const SECTORS = [
  'All',
  'Automobiles',
  'Capital Goods',
  'Chemicals',
  'Construction Materials',
  'Consumer Durables',
  'Consumer Services',
  'Financial Services',
  'Fast Moving Consumer Goods',
  'Healthcare',
  'Information Technology',
  'Metals & Mining',
  'Oil Gas & Consumable Fuels',
  'Power',
  'Realty',
  'Services',
  'Telecommunication',
];

const MarketExplorer = ({ onOpenSymbol }) => {
  const [filterMode, setFilterMode] = useState('letter'); // 'letter' or 'sector'
  const [selectedLetter, setSelectedLetter] = useState('R');
  const [selectedSector, setSelectedSector] = useState('Oil Gas & Consumable Fuels');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);

  const [loading, setLoading] = useState(false);
  const [instruments, setInstruments] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchDirectory = async (targetPage = page, targetPageSize = pageSize) => {
    setLoading(true);
    try {
      const offset = (targetPage - 1) * targetPageSize;
      const params = { limit: targetPageSize, offset };
      if (filterMode === 'letter') {
        params.letter = selectedLetter.toLowerCase();
      } else {
        if (selectedSector !== 'All') {
          params.sector = selectedSector;
        } else {
          params.letter = 'a';
        }
      }

      const res = await patternsRadarService.getInstruments(params);
      setInstruments(res.instruments || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('Failed to fetch instruments:', err);
      setInstruments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchDirectory(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMode, selectedLetter, selectedSector]);

  const totalPages = Math.ceil(total / pageSize) || 1;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    fetchDirectory(newPage, pageSize);
  };

  // Column-level header filters (CRM Theme)
  const [colFilterSymbol, setColFilterSymbol] = useState('');
  const [colFilterName, setColFilterName] = useState('');
  const [colFilterSector, setColFilterSector] = useState('');
  const [colFilterIndustry, setColFilterIndustry] = useState('');
  const [colFilterTier, setColFilterTier] = useState('');
  const [colFilterClose, setColFilterClose] = useState('');
  const [colFilter1D, setColFilter1D] = useState('');

  const hasActiveFilters = Boolean(
    colFilterSymbol ||
      colFilterName ||
      colFilterSector ||
      colFilterIndustry ||
      colFilterTier ||
      colFilterClose ||
      colFilter1D
  );

  const clearFilters = () => {
    setColFilterSymbol('');
    setColFilterName('');
    setColFilterSector('');
    setColFilterIndustry('');
    setColFilterTier('');
    setColFilterClose('');
    setColFilter1D('');
  };

  // Client-side quick filter
  const filteredRows = instruments.filter((inst) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchSearch =
        inst.symbol?.toLowerCase().includes(term) ||
        inst.name?.toLowerCase().includes(term) ||
        inst.industry?.toLowerCase().includes(term);
      if (!matchSearch) return false;
    }

    if (
      colFilterSymbol &&
      !inst.symbol?.toLowerCase().includes(colFilterSymbol.toLowerCase().trim())
    ) {
      return false;
    }

    if (
      colFilterName &&
      !inst.name?.toLowerCase().includes(colFilterName.toLowerCase().trim())
    ) {
      return false;
    }

    if (
      colFilterSector &&
      !inst.sector?.toLowerCase().includes(colFilterSector.toLowerCase().trim())
    ) {
      return false;
    }

    if (
      colFilterIndustry &&
      !inst.industry?.toLowerCase().includes(colFilterIndustry.toLowerCase().trim())
    ) {
      return false;
    }

    if (colFilterTier && String(inst.universe_tier) !== String(colFilterTier)) {
      return false;
    }

    if (!matchesNumericRange(inst.close, colFilterClose)) {
      return false;
    }

    if (colFilter1D === 'gainers' && (toNum(inst.ret_1d) ?? 0) <= 0) {
      return false;
    }

    if (colFilter1D === 'losers' && (toNum(inst.ret_1d) ?? 0) >= 0) {
      return false;
    }

    return true;
  });

  return (
    <div className="panel panel-default with-rounded-corner with-shadow">
      <div className="panel-heading border-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span className="panel-title font-weight-bold">
          <FontAwesomeIcon icon={faBuilding} className="text-primary me-2" />
          NSE Instrument Directory & Sector Heat Table
        </span>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="small text-muted">
            Showing <strong>{start}</strong> to <strong>{end}</strong> of <strong>{total}</strong> instruments
            {hasActiveFilters && ` (${filteredRows.length} on page match)`}
          </span>
          {total > 100 && (
            <div className="pagination-nav-buttons ms-1">
              <button
                type="button"
                className="btn btn-default btn-xs nav-prev-btn"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1 || loading}
                title="Previous"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                type="button"
                className="btn btn-default btn-xs nav-next-btn"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages || loading}
                title="Next"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
          <button
            type="button"
            className="btn btn-default btn-xs"
            onClick={() => fetchDirectory(page, pageSize)}
            disabled={loading}
            title="Refresh Table"
          >
            <FontAwesomeIcon icon={faRefresh} spin={loading} />
          </button>
        </div>
      </div>

      <div className="panel-body">
        {/* Filter Controls Bar */}
        <div className="row g-2 align-items-center mb-3">
          <div className="col-md-3">
            <div className="btn-group btn-group-sm w-100" role="group">
              <button
                type="button"
                className={`btn ${
                  filterMode === 'letter' ? 'btn-primary' : 'btn-default'
                }`}
                onClick={() => setFilterMode('letter')}
              >
                A–Z Index
              </button>
              <button
                type="button"
                className={`btn ${
                  filterMode === 'sector' ? 'btn-primary' : 'btn-default'
                }`}
                onClick={() => setFilterMode('sector')}
              >
                Sector Index
              </button>
            </div>
          </div>

          <div className="col-md-5">
            {filterMode === 'letter' ? (
              <div className="d-flex flex-wrap">
                {ALPHABET.map((char) => (
                  <button
                    key={char}
                    type="button"
                    className={`pr-alpha-btn ${
                      selectedLetter === char ? 'active' : ''
                    }`}
                    onClick={() => setSelectedLetter(char)}
                  >
                    {char}
                  </button>
                ))}
              </div>
            ) : (
              <select
                className="form-control form-control-sm"
                value={selectedSector}
                onChange={(e) => setSelectedSector(e.target.value)}
              >
                {SECTORS.map((s, idx) => (
                  <option key={idx} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="col-md-4">
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-white">
                <FontAwesomeIcon icon={faSearch} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by Symbol or Industry..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Directory Table */}
        {/* Directory Table */}
        <DoubleScrollTable dependencies={[filteredRows]}>
          <table className="table table-hover table-condensed bg-white m-b-0 sticky-table pr-table">
            <thead>
              <tr className="bg-light">
                <th style={{ minWidth: '120px' }}>Symbol</th>
                <th style={{ minWidth: '180px' }}>Company Name</th>
                <th style={{ minWidth: '150px' }}>Sector</th>
                <th style={{ minWidth: '150px' }}>Industry</th>
                <th style={{ minWidth: '80px' }} className="text-center">Tier</th>
                <th style={{ minWidth: '110px' }} className="text-center">Liquidity Rank</th>
                <th style={{ minWidth: '110px' }} className="text-end">Last Close</th>
                <th style={{ minWidth: '110px' }} className="text-end">1D Return</th>
                <th style={{ minWidth: '130px' }} className="text-end">20D Avg Turnover</th>
                <th style={{ minWidth: '80px' }} className="text-center">Action</th>
              </tr>
              <tr>
                <th>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={colFilterSymbol}
                    onChange={(e) => setColFilterSymbol(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={colFilterName}
                    onChange={(e) => setColFilterName(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={colFilterSector}
                    onChange={(e) => setColFilterSector(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={colFilterIndustry}
                    onChange={(e) => setColFilterIndustry(e.target.value)}
                  />
                </th>
                <th>
                  <select
                    value={colFilterTier}
                    onChange={(e) => setColFilterTier(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="1">T-1</option>
                    <option value="2">T-2</option>
                    <option value="3">T-3</option>
                  </select>
                </th>
                <th></th>
                <th>
                  <input
                    type="text"
                    placeholder="e.g. 100-500"
                    title="Enter single value or range (e.g. 100-500, >=100, <=500)"
                    value={colFilterClose}
                    onChange={(e) => setColFilterClose(e.target.value)}
                  />
                </th>
                <th>
                  <select
                    value={colFilter1D}
                    onChange={(e) => setColFilter1D(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="gainers">Gainers</option>
                    <option value="losers">Losers</option>
                  </select>
                </th>
                <th></th>
                <th className="text-center">
                  {hasActiveFilters ? (
                    <button
                      type="button"
                      className="btn btn-default btn-xs btn-clear-filter"
                      onClick={clearFilters}
                      title="Clear filters"
                    >
                      Clear
                    </button>
                  ) : null}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-muted">
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin
                      className="me-2 text-primary fs-5"
                    />
                    Loading NSE instruments directory...
                  </td>
                </tr>
              ) : filteredRows.length > 0 ? (
                filteredRows.map((inst, idx) => (
                  <tr key={idx}>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0 font-weight-bold text-primary"
                        onClick={() => onOpenSymbol && onOpenSymbol(inst.symbol)}
                      >
                        {inst.symbol}
                      </button>
                    </td>
                    <td
                      className="text-truncate"
                      style={{ maxWidth: '200px' }}
                      title={inst.name}
                    >
                      {inst.name || '-'}
                    </td>
                    <td>
                      <span className="badge bg-light text-dark border">
                        {inst.sector || '-'}
                      </span>
                    </td>
                    <td>{inst.industry || '-'}</td>
                    <td className="text-center">
                      <span className="badge bg-secondary">
                        {inst.universe_tier ? `T-${inst.universe_tier}` : '-'}
                      </span>
                    </td>
                    <td className="text-center font-weight-bold">
                      {inst.liquidity_rank ? `#${inst.liquidity_rank}` : '-'}
                    </td>
                    <td className="text-end font-weight-bold">
                      {formatCurrency(inst.last_close)}
                    </td>
                    <td
                      className={`text-end ${
                        (toNum(inst.ret_1d) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                      }`}
                    >
                      {formatPercent(inst.ret_1d, { showPlus: true })}
                    </td>
                    <td className="text-end">
                      ₹
                      {inst.avg_turnover_20d
                        ? (inst.avg_turnover_20d / 10000000).toFixed(2)
                        : '0.00'}{' '}
                      Cr
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-primary btn-xs"
                        onClick={() => onOpenSymbol && onOpenSymbol(inst.symbol)}
                        title={`View ${inst.symbol} Chart & Technicals`}
                      >
                        <FontAwesomeIcon icon={faChartLine} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-muted">
                    {hasActiveFilters
                      ? 'No instruments match the active column filters. (Click "Clear" to reset filters)'
                      : 'No instruments found for this index criteria.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </DoubleScrollTable>

        {/* Footer Pagination Bar */}
        {total > 0 && (
          <div className="d-flex justify-content-between align-items-center p-2 border-top bg-light flex-wrap gap-2">
            <div className="small text-muted">
              Showing <strong>{start}</strong> to <strong>{end}</strong> of <strong>{total}</strong> registered instruments
            </div>
            {total > 100 && (
              <div className="pagination-nav-buttons">
                <button
                  type="button"
                  className="btn btn-default btn-xs nav-prev-btn"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1 || loading}
                  title="Previous"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  type="button"
                  className="btn btn-default btn-xs nav-next-btn"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages || loading}
                  title="Next"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketExplorer;
